<template>
  <DropdownMenu v-model:open="menuOpen">
    <DropdownMenuTrigger as-child>
      <Button
        v-if="headerMode"
        variant="ghost"
        class="h-9 gap-1 rounded-full px-1.5 pl-1 hover:bg-muted"
      >
        <Avatar class="h-8 w-8 border border-border/60">
          <AvatarFallback class="text-xs font-medium">{{ userInitials }}</AvatarFallback>
        </Avatar>
        <ChevronDown class="h-4 w-4 shrink-0 text-muted-foreground" />
      </Button>
      <Button
        v-else
        size="icon"
        variant="outline"
        :class="trigger === 'more' ? 'min-h-[35px] min-w-[35px]' : 'rounded-full'"
      >
        <MoreVertical v-if="trigger === 'more'" class="h-4 w-4" />
        <User v-else class="h-5 w-5" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent class="w-64 rtl:ml-1.5 ltr:mr-1.5 mt-3">
      <DropdownMenuLabel class="font-normal">
        <div class="flex items-center justify-between gap-2">
          <div class="min-w-0">
            <p class="text-sm font-medium leading-none capitalize select-none truncate">
              {{ authStore.user?.name || "User" }}
            </p>
            <p class="text-xs text-muted-foreground mt-1 select-none truncate">
              {{ authStore.user?.email || "No email" }}
            </p>
          </div>
          <div class="flex flex-col items-end gap-0.5 shrink-0">
            <div class="px-2 py-1 rounded-full bg-primary/10 border border-primary">
              <p class="text-xs font-medium capitalize select-none">
                {{ authStore.user?.role || "No role" }}
              </p>
            </div>
            <span
              class="text-[10px] leading-none text-muted-foreground/80 select-none"
              :title="uiVersionFull"
            >{{ uiVersionLabel }}</span>
          </div>
        </div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem class="flex items-center select-none" @click="navigateToProfile">
          <User class="mr-2 h-4 w-4" />
          <span class="select-none">Profile</span>
        </DropdownMenuItem>
        <DropdownMenuItem class="flex items-center select-none" @click="navigateToResetPassword">
          <LockIcon class="mr-2 h-4 w-4" />
          <span class="select-none">{{ $t('auth.resetPassword.title') }}</span>
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />

      <div class="px-2 pb-2">
        <div class="space-y-2">
          <h4 class="font-medium leading-none select-none">{{ $t('sidebar.settings') }}</h4>
        </div>
        <div class="grid gap-2 mt-2">
          <div class="flex gap-2">
            <LanguageSwitcher class="flex-1" />
            <ThemeSwitcher class="flex-1" />
            <NotificationNavigation />
          </div>
          <Button class="w-full" @click="handleLogout">
            {{ $t('sidebar.logout') }}
          </Button>
        </div>
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useRouter } from "vue-router";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu/index";
import { User, MoreVertical, Lock as LockIcon, ChevronDown } from "lucide-vue-next";
import { Avatar, AvatarFallback } from "@/shared/components/ui/avatar";
import { useAuthStore } from "@/core/auth/auth";
import NotificationNavigation from "./NotificationNavigation.vue";
import LanguageSwitcher from "./LanguageSwitcher.vue";
import ThemeSwitcher from "./ThemeSwitcher.vue";
import { useI18n } from 'vue-i18n';
import { getAppUiVersion, getAppUiVersionLabel } from '@/core/app/uiVersion';

withDefaults(
  defineProps<{
    trigger?: 'user' | 'more';
    headerMode?: boolean;
  }>(),
  {
    trigger: 'user',
    headerMode: false
  }
);

const authStore = useAuthStore();
const router = useRouter();

const menuOpen = ref(false);
const { locale } = useI18n();
const uiVersionLabel = getAppUiVersionLabel();
const uiVersionFull = getAppUiVersion();

const userInitials = computed(() => {
  const n = (authStore.user?.name || 'User').trim();
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return n.slice(0, 2).toUpperCase() || 'U';
});

watch(locale, () => {
  menuOpen.value = false;
});

const handleLogout = async () => {
  await authStore.logout();
};

const navigateToProfile = () => {
  router.push("/profile");
};

const navigateToResetPassword = () => {
  router.push("/profile/reset-password");
};
</script>
